/**
 * STEPERP Mobil — paylaşılan primitifler (GENTAŞ Saha referansı: design_source_mobil).
 * YALNIZ mobil kabuk (`.m-app`, ≤767px) içinde kullanılır. Masaüstü karşılıkları:
 *   MRow ↔ tablo satırı · SidePanel ↔ Drawer · Sheet ↔ dropdown · MSelect ↔ <select>.
 * Tasarım dili: kart odaklı, tablo yok; dokunma ≥44px, sayı mono+tabular, TR format.
 */
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { createPortal } from "react-dom";
import { Icon } from "../ui/Icon";

/** Escape ile kapatma — aktifken pencere `Escape` tuşunu dinler (modal/panel/sheet). */
export function useEsc(active: boolean, fn: () => void) {
  useEffect(() => {
    if (!active) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") fn();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [active, fn]);
}

/* ── Kart ── */
export function MCard({
  className = "",
  pad = false,
  style,
  onClick,
  children,
}: {
  className?: string;
  pad?: boolean;
  style?: CSSProperties;
  onClick?: () => void;
  children: ReactNode;
}) {
  return (
    <div className={`m-card ${pad ? "m-card-p" : ""} ${className}`.trim()} style={style} onClick={onClick}>
      {children}
    </div>
  );
}

/* ── Liste satırı (ikon + başlık/alt + sağ + chevron) ── */
export function MRow({
  icon,
  tone = "var(--primary)",
  toneBg = "var(--primary-subtle)",
  title,
  sub,
  right,
  chevron = true,
  onClick,
}: {
  icon?: string;
  tone?: string;
  toneBg?: string;
  title: ReactNode;
  sub?: ReactNode;
  right?: ReactNode;
  chevron?: boolean;
  onClick?: () => void;
}) {
  return (
    <button type="button" className="m-row" onClick={onClick}>
      {icon && (
        <span className="m-ric" style={{ background: toneBg, color: tone }}>
          <Icon name={icon} size={17} />
        </span>
      )}
      <span className="m-rt">
        <b>{title}</b>
        {sub != null && <span>{sub}</span>}
      </span>
      <span className="m-rr">{right}</span>
      {chevron && (
        <span className="m-chev">
          <Icon name="chevRight" size={16} />
        </span>
      )}
    </button>
  );
}

/* ── Anahtar–değer satırı ── */
export function KV({ k, v, mono = false }: { k: ReactNode; v: ReactNode; mono?: boolean }) {
  return (
    <div className="m-kv">
      <span>{k}</span>
      <b className={mono ? "mono" : ""}>{v}</b>
    </div>
  );
}

/* ── Alttan açılan sayfa (bottom sheet) ── */
export function Sheet({
  open,
  title,
  onClose,
  children,
  footer,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useEsc(open, onClose);
  return (
    <>
      <div className={`m-scrim ${open ? "open" : ""}`} style={{ zIndex: 44 }} onClick={onClose} />
      <div className={`m-sheet ${open ? "open" : ""}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="m-grab" />
        <div className="m-sheet-h">
          <b>{title}</b>
          <button
            type="button"
            className="m-iconbtn"
            style={{ color: "var(--neutral-500)", width: 40, height: 40 }}
            onClick={onClose}
            aria-label="Kapat"
          >
            <Icon name="x" size={18} />
          </button>
        </div>
        <div className="m-sheet-b">{children}</div>
        {footer && <div className="m-sheet-f">{footer}</div>}
      </div>
    </>
  );
}

/* ── Sağdan açılan panel (masaüstü Drawer'ın mobil karşılığı) ── */
export function SidePanel({
  open,
  title,
  sub,
  onClose,
  children,
  footer,
}: {
  open: boolean;
  title: string;
  sub?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useEsc(open, onClose);
  return (
    <>
      <div className={`m-scrim ${open ? "open" : ""}`} style={{ zIndex: 45 }} onClick={onClose} />
      <div className={`m-side ${open ? "open" : ""}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="m-side-h">
          <div style={{ flex: 1, minWidth: 0 }}>
            <b>{title}</b>
            {sub != null && <small>{sub}</small>}
          </div>
          <button
            type="button"
            className="m-iconbtn"
            style={{ color: "var(--neutral-500)", width: 40, height: 40 }}
            onClick={onClose}
            aria-label="Kapat"
          >
            <Icon name="x" size={18} />
          </button>
        </div>
        <div className="m-side-b">{children}</div>
        {footer && <div className="m-side-f">{footer}</div>}
      </div>
    </>
  );
}

/* ── Yatay sürükle/kaydır filtre şeridi (touch + fare) ── */
export function Chips({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const moved = useRef(false);
  const onDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el || el.scrollWidth <= el.clientWidth) return;
    const x0 = e.clientX;
    const sl0 = el.scrollLeft;
    moved.current = false;
    const mv = (ev: PointerEvent) => {
      const dx = ev.clientX - x0;
      if (Math.abs(dx) > 6) moved.current = true;
      el.scrollLeft = sl0 - dx;
    };
    const up = () => {
      window.removeEventListener("pointermove", mv);
      window.removeEventListener("pointerup", up);
      setTimeout(() => {
        moved.current = false;
      }, 0);
    };
    window.addEventListener("pointermove", mv);
    window.addEventListener("pointerup", up);
  };
  return (
    <div
      className="m-chips"
      ref={ref}
      onPointerDown={onDown}
      onClickCapture={(e) => {
        if (moved.current) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      {children}
    </div>
  );
}

/* ── Alttan açılan seçim (native <select> yerine — tasarım diline uygun) ── */
export function MSelect({
  label,
  required,
  labelExtra,
  value,
  placeholder = "— Seçin —",
  options,
  onChange,
  disabled,
  help,
  style,
}: {
  label?: string;
  required?: boolean;
  labelExtra?: ReactNode;
  value: string;
  placeholder?: string;
  options: string[];
  onChange: (v: string) => void;
  disabled?: boolean;
  help?: ReactNode;
  style?: CSSProperties;
}) {
  const [open, setOpen] = useState(false);
  useEsc(open, () => setOpen(false));
  const host = typeof document !== "undefined" ? document.querySelector(".m-app") : null;
  return (
    <div className="field" style={style}>
      {label && (
        <label className="field-label" style={labelExtra ? { display: "flex" } : undefined}>
          {label}
          {required && <span className="m-req"> *</span>}
          {labelExtra}
        </label>
      )}
      <button
        type="button"
        className={`m-input m-selbtn ${disabled ? "dis" : ""}`}
        disabled={disabled}
        onClick={() => setOpen(true)}
        aria-haspopup="listbox"
        aria-label={label || placeholder}
      >
        <span className={value ? "" : "ph"}>{value || placeholder}</span>
        <Icon name="chevDown" size={15} />
      </button>
      {help && <div className="m-help">{help}</div>}
      {open &&
        host &&
        createPortal(
          <>
            <div className="m-scrim open" style={{ zIndex: 58 }} onClick={() => setOpen(false)} />
            <div className="m-sheet open m-optsheet" role="listbox" aria-label={label || placeholder}>
              <div className="m-grab" />
              <div className="m-sheet-h">
                <b>{label || placeholder}</b>
                <button
                  type="button"
                  className="m-iconbtn"
                  style={{ color: "var(--neutral-500)", width: 40, height: 40 }}
                  onClick={() => setOpen(false)}
                  aria-label="Kapat"
                >
                  <Icon name="x" size={18} />
                </button>
              </div>
              <div className="m-sheet-b" style={{ padding: "8px 10px 44px" }}>
                {options.map((o) => (
                  <button
                    type="button"
                    key={o}
                    className={`m-opt ${o === value ? "sel" : ""}`}
                    role="option"
                    aria-selected={o === value}
                    onClick={() => {
                      onChange(o);
                      setOpen(false);
                    }}
                  >
                    <span>{o}</span>
                    {o === value && <Icon name="check" size={16} />}
                  </button>
                ))}
              </div>
            </div>
          </>,
          host,
        )}
    </div>
  );
}

/* ── Onay/proses zinciri (dikey adım listesi) ── */
export interface ChainStep {
  role: ReactNode;
  who: ReactNode;
  t?: ReactNode;
  st: "done" | "act" | "wait" | "rej";
}
export function Chain({ steps }: { steps: ChainStep[] }) {
  return (
    <div className="m-chain">
      {steps.map((s, i) => (
        <div key={i} className={`cs ${s.st}`}>
          <span className="ci">
            {s.st === "done" ? (
              <Icon name="check" size={13} />
            ) : s.st === "act" ? (
              <Icon name="clock" size={13} />
            ) : s.st === "rej" ? (
              <Icon name="x" size={13} />
            ) : (
              <i />
            )}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: s.st === "wait" ? "var(--neutral-400)" : "var(--neutral-900)",
                }}
              >
                {s.role}
              </span>
              {s.t && (
                <span className="mono" style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--neutral-400)" }}>
                  {s.t}
                </span>
              )}
              {s.st === "act" && (
                <span className="badge b-primary no-dot" style={{ marginLeft: "auto" }}>
                  Sırada
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "var(--neutral-500)", marginTop: 1 }}>{s.who}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Tek-mesaj toast (referans modeli: aynı anda tek bildirim, 2.6sn otomatik kapanır) ──
   Kullanım: const { toast, toastNode } = useMToast();  … {toastNode} en sonda render edilir. */
export function useMToast() {
  const [msg, setMsg] = useState<ReactNode>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toast = (m: ReactNode) => {
    if (timer.current) clearTimeout(timer.current);
    setMsg(m);
    timer.current = setTimeout(() => setMsg(null), 2600);
  };
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);
  const toastNode = msg ? (
    <div className="m-toast" role="status">
      {msg}
    </div>
  ) : null;
  return { toast, toastNode };
}
